<template>
  <div id="app" :class="['app-wrapper', currentLanguage.code]">
    <div v-show="!isAuthReady">
      <AryaPreAuthComponent />
    </div>
    <template v-if="isAuthReady">
      <app-background></app-background>
      <navigation
      @toggle-product-selection="handleProductSelectVisibility(true)"
      />
      <banner></banner>
      <main-loader></main-loader>
      <custom-notification></custom-notification>
      <language-selector></language-selector>
      <file-uploader></file-uploader>
      <full-screen-video></full-screen-video>
      <player-preview-popup></player-preview-popup>
      <debug-panel v-if="$currentUser.hasGroupDebug()"></debug-panel>
      <div id="app-content" :class="appContentClass">
        <side-navigation v-show="showSideNavigation"></side-navigation>
        <!-- FIX ME: Condition and div can be completely removed after new design had been implemented as a whole -->
        <div v-if="$route.name === 'activation'" class="licenses-theme-line-background"></div>
        <div class="main-component">
          <alert-banner>
            <BaseIconSvg name="flowbite:close-outline" />
          </alert-banner>
          <router-view :key="$route.name"></router-view>
        </div>
      </div>

      <BaseSideMenu
        class="!bg-spinetix-900 !h-full !absolute z-[999] top-0 left-0 bottom-0"
        :menu-data="{ groups: [], activeRouteName: '' }"
        :product="selectedProduct"
        :isVisible="isProductSelectVisible"
        :showProductsNav="true"
        :userRights="userRights"
        :userEnabledProducts="userEnabledProducts"
        @on-back-to-app="handleProductSelectVisibility(false)"
        @product-select="handleProductSelect"
      >
        <template #nav-header>
          <div
            class="side-nav-logo w-64 cursor-pointer select-none flex items-center px-4 bg-spinetix-900 h-16"
            data-component-element-id="side-nav-logo"
            @click="handleProductSelectVisibility(false)"
          >
            <BaseIconSvg
              class="h-9!"
              :name="isActivate ? 'logo:activate-lockup' : 'logo:arya-lockup'"
            />
          </div>
        </template>
        <template #footer>
          <div class="flex flex-col items-start gap-3 w-full p-4 pt-6 mt-2 bg-primary-50 rounded text-start">
            <div class="w-full flex justify-center">
              <div class="px-2.5 py-0.5  text-xs font-semibold bg-primary-600  text-white rounded items-center justify-center">
                <span>{{ $t('texts.welcomeToHUB') }}</span>
              </div>
            </div>
            <p class="text-sm">{{ $t('texts.welcomeToHUBDesc') }}</p>
            <a
              v-if="hubHelpUrl"
              class="text-sm underline text-primary-600 capitalize"
              target="_blank"
              :href="hubHelpUrl"
            >
              {{ $t('label.learnMore') }}
            </a>
          </div>
        </template>
      </BaseSideMenu>
      <BaseLiveChatButton
        :enabled="hasZendesk"
        :language-code="$dataStore.getLanguage().code"
      />
    </template>
  </div>
</template>

<script>
import EventBus from '@/components/mixins/event-bus-mixin'
import { PRODUCT_ARYA, PRODUCT_ACTIVATE } from '@base-shared/utils/constants'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth'
import { useGroupStore } from '@base-shared/stores/group';

export default {
  name: 'app',
  inject: [
    '$currentUser',
    '$utils',
    '$dataStore',
    '$signedInUserInfo'
  ],
  watch: {
    '$route' (routeTo) {
      this.handleContentAppContentColor(routeTo.name)

      // Close product selection side menu on route change
      this.handleProductSelectVisibility(false)
    },
    /**
     * Watches the `groupStore.groupInfo` for changes. Replacement
     * for the old `BIND_USER_INFO_IN_VUE` from EventBus.
     */
    'groupStore.groupInfo': {
      handler (newValue) {
        // Since $signedInUserInfo is not reactive, utilize event for userInfo
        this.isActivePlanSuspended = !!newValue?.activePlan?.suspended

        // Check if the user has access to the product
        this.hasProductAccess = this.$currentUser.hasAccessToSelectedProduct()
        if (!this.hasProductAccess) {
          this.$router.noProductAccessRedirection()
        }

        // Trigger user info mapping on user info update event
        this.mapUserPageInfo()
      },
      deep: true
    }
  },
  data () {
    return {
      currentLanguage: this.$dataStore.getLanguage(),
      appContentClass: {
        'grey-background': false,
        'white-background': false,
        licensesTheme: false
      },
      isActivePlanSuspended: false,
      hasProductAccess: false,

      isActivate: false, // Flag if current page is Activate or Arya
      isProductSelectVisible: false,
      userRights: [],
      userEnabledProducts: [],
      hasZendesk: !!CONFIG?.ZENDESK_KEY
    }
  },
  computed: {
    showSideNavigation  () {
      // For Activate, side navigation should be shown regardless of account suspension as long as user has product access
      if (this.$utils.isPageForLicenses()) {
        return this.hasProductAccess
      }

      return !this.isActivePlanSuspended && this.hasProductAccess
    },
    selectedProduct () {
      return this.isActivate ? PRODUCT_ACTIVATE : PRODUCT_ARYA;
    },
    hubHelpUrl () {
      return CONFIG?.HAS_HUB
        ? 'https://arya-support.spinetix.cloud/hc/en-us/articles/38182657375633-New-to-SpinetiX-HUB-Here-is-Everything-You-Need-to-Know'
        : null
    },
    isAuthReady () {
      return useCognitoAuthStore().isAuthReady;
    }
  },
  mixins: [],
  setup () {
    // Expose the store to the Options API context.
    return { groupStore: useGroupStore() };
  },
  methods: {
    handleContentAppContentColor (routeName) {
      const appContentClass = this.$utils.clone(this.appContentClass)

      appContentClass['grey-background'] = false
      appContentClass.licensesTheme = false

      if (this.$utils.getPageNamesWithGreyBackgroundColor().includes(routeName)) {
        appContentClass['grey-background'] = true
      }

      if (this.$utils.isPageForLicenses(routeName)) {
        appContentClass.licensesTheme = true
        appContentClass['white-background'] = !['signIn', 'settings', 'activateUserProfile', 'account-switched', 'noProductAccess'].includes(routeName)
        appContentClass['grey-background'] = routeName === 'noProductAccess'
      }

      this.appContentClass = appContentClass
    },
    mapUserPageInfo () {
      // Determine if the current page is Activate
      this.isActivate = this.$utils.isPageForLicenses()

      // Map user rights and enabled products directly
      // from currentUser instance to match token data.
      // Empty for no session or no data.
      this.userRights = this.$currentUser.getUserGroups() ?? []
      this.userEnabledProducts = this.$currentUser.getProductEntitlements() ?? []
    },
    handleProductSelectVisibility (isVisible) {
      this.isProductSelectVisible = isVisible
    },
    handleProductSelect (product) {
      // Close the product selection side menu
      this.handleProductSelectVisibility(false)

      // Redirect to the selected product
      const url = this.$utils.getProductUrlsFromConfig(product);
      if (!url) {
        console.warn(`No URL found for product: ${product}`);
        return;
      }

      window.location.href = url;
    }
  },
  mounted () {
    // Inject language to html tag
    const getLang = this.$dataStore.getLanguage()
    this.$dataStore.injectLangInHTMLTag(getLang.code)

    this.handleContentAppContentColor(this.$route.name)

    // Handle suspended active plan from userInfo
    this.isActivePlanSuspended = !!this.$signedInUserInfo?.activePlan?.suspended

    // Check if the user has access to the product
    this.mapUserPageInfo()

    EventBus.$on('SIGNED_IN', () => {
      // Trigger user info mapping on sign in event
      this.mapUserPageInfo()
    })
  }
}
</script>

<style lang="scss" src="./static/style/base.scss"></style>

<style lang="css" src="animate.css/animate.compat.css"></style>
<style lang="scss" src="./tailwind.scss"></style>
